import React, { useState } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getMyJobs, getAppliedJobs } from '../api/job';
import { Job, JobStatus } from '../types/job';
import { useAuth } from '../context/AuthContext';

export async function myJobsLoader() {
  const [jobs, appliedJobs] = await Promise.all([
    getMyJobs().catch(() => [] as Job[]),
    getAppliedJobs().catch(() => [] as Job[]),
  ]);
  return { jobs, appliedJobs };
}

const statusConfig: Record<JobStatus, { label: string; colour: string }> = {
  Open:       { label: 'Open',        colour: 'bg-green-100 text-green-800' },
  InProgress: { label: 'In Progress', colour: 'bg-blue-100 text-blue-800' },
  Completed:  { label: 'Completed',   colour: 'bg-surface-container text-on-surface-variant' },
  Incomplete: { label: 'Incomplete',  colour: 'bg-red-100 text-red-800' },
  Cancelled:  { label: 'Cancelled',   colour: 'bg-surface-container text-on-surface-variant' },
  Expired:         { label: 'Expired',      colour: 'bg-surface-container text-on-surface-variant' },
  'Under Review':  { label: 'Under Review', colour: 'bg-yellow-100 text-yellow-800' },
};

const activeStatuses: JobStatus[] = ['Open', 'InProgress'];
const closedStatuses: JobStatus[] = ['Completed', 'Incomplete', 'Cancelled', 'Expired'];

type Tab = 'active' | 'applied' | 'closed';

const JobCard: React.FC<{ job: Job; isContractor: boolean }> = ({ job, isContractor }) => {
  const s = statusConfig[job.status];
  return (
    <Link
      to={`/jobs/${job.id}`}
      className="block bg-white rounded-xl border border-outline-variant/20 p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="font-headline-sm text-primary truncate">{job.title}</h2>
          <div className="flex flex-wrap gap-2 mt-2 text-sm text-on-surface-variant">
            <span className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">location_on</span>
              {job.location}
            </span>
            {job.categoryName && (
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">category</span>
                {job.categoryName}
              </span>
            )}
          </div>
          {job.status === 'InProgress' && (
            <p className="mt-2 text-sm text-blue-700">
              {job.clientConfirmedCompletion && job.contractorConfirmedCompletion
                ? 'Both parties confirmed - completing...'
                : isContractor
                ? job.contractorConfirmedCompletion
                  ? 'You confirmed - waiting for client'
                  : job.clientConfirmedCompletion
                  ? 'Client confirmed - awaiting your confirmation'
                  : 'Awaiting completion confirmation'
                : job.clientConfirmedCompletion
                ? 'Waiting for contractor to confirm'
                : job.contractorConfirmedCompletion
                ? 'Contractor confirmed - waiting for you'
                : 'Awaiting completion confirmation'}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          <span className={`px-2.5 py-1 rounded-full text-xs font-label-bold ${s.colour}`}>
            {s.label}
          </span>
        </div>
      </div>
    </Link>
  );
};

const MyJobsPage: React.FC = () => {
  const { jobs, appliedJobs } = useLoaderData() as { jobs: Job[]; appliedJobs: Job[] };
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isContractor = currentUser?.isContractor ?? false;
  const [tab, setTab] = useState<Tab>('active');

  const tabCounts: Record<Tab, number> = {
    active: jobs.filter(j => activeStatuses.includes(j.status)).length,
    applied: appliedJobs.length,
    closed: jobs.filter(j => closedStatuses.includes(j.status)).length,
  };

  const tabs: { id: Tab; label: string }[] = isContractor
    ? [
        { id: 'active', label: 'Active' },
        { id: 'applied', label: 'Applied' },
        { id: 'closed', label: 'Closed' },
      ]
    : [
        { id: 'active', label: 'Active' },
        { id: 'closed', label: 'Closed' },
      ];

  const displayed =
    tab === 'applied'
      ? appliedJobs
      : jobs.filter(j =>
          tab === 'active' ? activeStatuses.includes(j.status) : closedStatuses.includes(j.status)
        );

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-24 md:pb-0">
      <TopAppBar />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline-md text-headline-md text-primary">My Jobs</h1>
            {isContractor && (
              <p className="text-sm text-on-surface-variant mt-1">Jobs you have been selected for or applied to</p>
            )}
          </div>
          {!isContractor && (
            <button
              onClick={() => navigate('/jobs/new')}
              className="bg-primary text-on-primary px-4 py-2 rounded-lg font-label-bold hover:bg-primary/90 transition-colors flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Post Job
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-outline-variant mb-6">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-5 py-2.5 font-label-bold capitalize transition-colors border-b-2 -mb-px ${
                tab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {label}
              <span className="ml-1.5 text-xs bg-surface-container px-1.5 py-0.5 rounded-full">
                {tabCounts[id]}
              </span>
            </button>
          ))}
        </div>

        {displayed.length === 0 ? (
          <div className="text-center py-12 text-on-surface-variant">
            {tab === 'active' && isContractor && (
              <>
                <span className="material-symbols-outlined text-[48px] mb-3 block text-outline">work_history</span>
                <p className="mb-2">No active jobs assigned to you yet.</p>
                <p className="text-sm">
                  Browse the{' '}
                  <Link to="/jobs" className="text-primary underline">job board</Link>
                  {' '}and message clients to get selected.
                </p>
              </>
            )}
            {tab === 'active' && !isContractor && (
              <>
                <p className="mb-4">No active jobs.</p>
                <button
                  onClick={() => navigate('/jobs/new')}
                  className="bg-primary text-on-primary px-5 py-2.5 rounded-lg font-label-bold"
                >
                  Post your first job
                </button>
              </>
            )}
            {tab === 'applied' && (
              <>
                <span className="material-symbols-outlined text-[48px] mb-3 block text-outline">send</span>
                <p className="mb-2">No applications yet.</p>
                <p className="text-sm">
                  Browse the{' '}
                  <Link to="/jobs" className="text-primary underline">job board</Link>
                  {' '}and contact clients to apply.
                </p>
              </>
            )}
            {tab === 'closed' && <p>No closed jobs yet.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            {displayed.map(job => (
              <JobCard key={job.id} job={job} isContractor={isContractor} />
            ))}
          </div>
        )}
      </main>

      <BottomNavBar />
    </div>
  );
};

export default MyJobsPage;
